/**
 * DEFUSE S15 — typed property descriptors for the simple geometry kinds
 * (Line / Point / Circle / Arc). Special (geometry) fields only; the common
 * object properties are attached by `buildCommonDescriptors`.
 *
 * Point-coord X/Y edits carry `zPreserve` (re-emit the full point incl. Z so a
 * 2-D edit does not flatten a non-zero elevation — residual #317, mirrors
 * `readLineState` / `reconcileCircleArcPatchWithDocument` withOptionalZ) and
 * `docPrecisionReconcile` (a formatNumber-rounded re-dispatch of an unrelated
 * row must not clobber the stored full-precision value).
 *
 * DISPLAY correctness (mirrors dxfGeometry.buildLine/Point/Arc/CirclePreview,
 * verified against source):
 *   - Line Length is the 3-D endpoint distance; Angle is atan2 in DEGREES.
 *   - Point rotation (DXF group 50) is DEGREES — stored + displayed VERBATIM
 *     (identity descriptor, mirrors the TEXT/MText Rotation `num` descriptor;
 *     import/export carry group 50 verbatim, so `rotation` is in file units).
 *   - Arc Total angle uses `normalizeAngleSpan` (degree span + wraparound); Arc
 *     length is `π·r·span/180`. Arc start/end angles are already degrees.
 */

import type {
  ArcEntity,
  CircleEntity,
  LineEntity,
  PointEntity,
} from "@dxf-ai-editor/drawing-model";

// normalizeAngleSpan comes from the LEAF module, NOT the `simpleGeometry`
// barrel: the barrel `export *`s `extendedPropertyMutations`, whose S15 Slice A
// typed-writer mirror imports the descriptor registry — the barrel edge here
// closed that loop at runtime (descriptor tables built against a partially
// initialized module graph → every Line/Circle/Arc edit rejected).
import { normalizeAngleSpan } from "../geometryEntityTypes";
import { calc, coord, num, type TypedPropertyDescriptor } from "./types";

const XY_PRESERVE = { zPreserve: true, docPrecisionReconcile: true } as const;
const Z_AXIS = { docPrecisionReconcile: true } as const;
const RAD_TO_DEG = 180 / Math.PI;

export const lineDescriptors: TypedPropertyDescriptor[] = [
  coord<LineEntity>("Start X", "start.x", (e) => e.start.x, XY_PRESERVE),
  coord<LineEntity>("Start Y", "start.y", (e) => e.start.y, XY_PRESERVE),
  coord<LineEntity>("Start Z", "start.z", (e) => e.start.z ?? 0, Z_AXIS),
  coord<LineEntity>("End X", "end.x", (e) => e.end.x, XY_PRESERVE),
  coord<LineEntity>("End Y", "end.y", (e) => e.end.y, XY_PRESERVE),
  coord<LineEntity>("End Z", "end.z", (e) => e.end.z ?? 0, Z_AXIS),
  // Appendix B pureUiKeys — 3-D length + degree angle (default format = formatNumber).
  calc<LineEntity>("Length", (e) =>
    Math.hypot(e.end.x - e.start.x, e.end.y - e.start.y, (e.end.z ?? 0) - (e.start.z ?? 0)),
  ),
  calc<LineEntity>("Angle", (e) =>
    Math.atan2(e.end.y - e.start.y, e.end.x - e.start.x) * RAD_TO_DEG,
  ),
];

export const pointDescriptors: TypedPropertyDescriptor[] = [
  // S15 Slice B — Point is typed-writer WRITABLE via the extended-geometry
  // cutover (`TYPED_EXTENDED_GEOMETRY_KINDS` + geometryReducer Step 1d); the
  // shell mirrors through `extendedPatchableLookup.Point` +
  // `typedExtendedGeometryShellRejects`. Grip drag is ALSO wired (S12b —
  // cad-core's typed `pointGrips` render a single "location" grip;
  // `gripDragTranslate.ts`'s "Point" case commits it through Position X/Y).
  coord<PointEntity>("Position X", "location.x", (e) => e.location.x, XY_PRESERVE),
  coord<PointEntity>("Position Y", "location.y", (e) => e.location.y, XY_PRESERVE),
  coord<PointEntity>("Position Z", "location.z", (e) => e.location.z ?? 0, Z_AXIS),
  // Rotation is DXF group 50 = DEGREES (Autodesk group-code-value-types spec +
  // ezdxf) — stored + displayed VERBATIM, identity like TEXT/MText Rotation.
  // Import (entityParser POINT_GROUP_READERS 50) + export (dxfBridge) are
  // verbatim, so `rotation` holds the file's degree value; a ×180/π projection
  // here would silently ~57× the exported group 50 (S15 Slice B P2).
  num<PointEntity>("Rotation", "rotation", (e) => e.rotation),
];

export const circleDescriptors: TypedPropertyDescriptor[] = [
  coord<CircleEntity>("Center X", "center.x", (e) => e.center.x, XY_PRESERVE),
  coord<CircleEntity>("Center Y", "center.y", (e) => e.center.y, XY_PRESERVE),
  coord<CircleEntity>("Center Z", "center.z", (e) => e.center.z ?? 0, Z_AXIS),
  num<CircleEntity>("Radius", "radius", (e) => e.radius, { exclusiveMin: 0 }),
  calc<CircleEntity>("Diameter", (e) => e.radius * 2),
  calc<CircleEntity>("Circumference", (e) => Math.PI * (e.radius * 2)),
  calc<CircleEntity>("Area", (e) => Math.PI * e.radius * e.radius),
];

export const arcDescriptors: TypedPropertyDescriptor[] = [
  coord<ArcEntity>("Center X", "center.x", (e) => e.center.x, XY_PRESERVE),
  coord<ArcEntity>("Center Y", "center.y", (e) => e.center.y, XY_PRESERVE),
  coord<ArcEntity>("Center Z", "center.z", (e) => e.center.z ?? 0, Z_AXIS),
  num<ArcEntity>("Radius", "radius", (e) => e.radius, { exclusiveMin: 0 }),
  // Arc angles are stored + displayed as DEGREES (dxfGeometry.buildArcPreview).
  num<ArcEntity>("Start angle", "startAngle", (e) => e.startAngle),
  num<ArcEntity>("End angle", "endAngle", (e) => e.endAngle),
  calc<ArcEntity>("Total angle", (e) => normalizeAngleSpan(e.startAngle, e.endAngle)),
  calc<ArcEntity>(
    "Arc length",
    (e) => (Math.PI * e.radius * normalizeAngleSpan(e.startAngle, e.endAngle)) / 180,
  ),
];
